import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation, Button } from '@packages/utils';
import { ENotificationType } from '@idaho-aeyc/ui-kit';
import PageWrapper from 'components/Base/PageWrapper';
import {
  ETableStoragePaths,
  TColumnFilterItem,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import useTable from 'components/Base/Table/hooks/useTable';
import { EIconNames } from 'components/Base/Icon';
import CardContent from 'components/Base/Layout/Content/CardContent';
import {
  EWorkshopSubscribersColumnKey,
  TWorkshopSubscriberDataUI,
} from 'data/types/subscribers.types.';
import {
  useGetWorkshopSubscribersCitiesQuery,
  useGetWorkshopSubscribersQuery,
  useExportNotifyMeMutation,
} from 'redux/apiSlice/subscibers.slice';
import { ETagType } from 'data/types/tags.types';
import { useGetTagsListQuery } from 'redux/apiSlice/tags.slice';
import { Table } from 'components/Base/Table';
import { TRtkErrorType } from 'data/types/generic.types';
import ExportDropdown from 'components/Shared/ExportDropdown';
import { useShowToastNotification } from 'components/Base/Notification';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import useGetColumnsData from './WorkshopSubscribersList.data';
import CreateWorkshopSubscribersModal from './CreateWorkshopSubscribersModal';

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  creationDate: 'registrationDate',
  [EWorkshopSubscribersColumnKey.FIRST_NAME]: 'firstName',
  [EWorkshopSubscribersColumnKey.LAST_NAME]: 'lastName',
  phone: 'phone',
  [EWorkshopSubscribersColumnKey.EMAIL]: 'email',
  [EWorkshopSubscribersColumnKey.AGE_GROUP]: 'ageGroups',
  [EWorkshopSubscribersColumnKey.CITY]: 'cities',
  [EWorkshopSubscribersColumnKey.TAG]: 'tags',
};

const SORT_FIELD_MAP: Record<string, string> = {
  creationDate: 'CREATION_DATE',
  [EWorkshopSubscribersColumnKey.FIRST_NAME]: 'FIRST_NAME',
  [EWorkshopSubscribersColumnKey.LAST_NAME]: 'LAST_NAME',
};

const WorkshopSubscribersList = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.subscribers.list',
  });

  const { defaultColumns } = useGetColumnsData();
  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasSelectedFilter,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TWorkshopSubscriberDataUI>({
    storagePath: ETableStoragePaths.SUBSCRIBERS_NOTIFY_ME,
    defaultColumns,
  });
  const [open, setOpen] = useState(false);

  const { data, isFetching, isLoading, error } = useGetWorkshopSubscribersQuery(
    {
      page,
      pageSize,
      sortColumn,
      filters: selectedFilters,
    },
  );
  const { data: citiesFilters, isSuccess: citiesFiltersSuccess } =
    useGetWorkshopSubscribersCitiesQuery('filter');

  const { data: tagsFilters, isSuccess: tagsFiltersSuccess } =
    useGetTagsListQuery(ETagType.SUBSCRIBER);

  useEffect(() => {
    if (citiesFiltersSuccess || tagsFiltersSuccess) {
      setColumns(
        columns.map(item => {
          if (item.key === EWorkshopSubscribersColumnKey.CITY) {
            item.filters = citiesFilters as TColumnFilterItem[];
          }
          if (
            item.key === EWorkshopSubscribersColumnKey.TAG &&
            tagsFiltersSuccess
          ) {
            item.filters = tagsFilters;
            item.filterSearch = true;
          }

          return item;
        }),
      );
    }
  }, [citiesFilters, tagsFiltersSuccess]);

  const isEmpty = !data?.content.length && !hasSelectedFilter && !isFetching;

  const [exportNotifyMe, { isLoading: isExporting }] =
    useExportNotifyMeMutation();
  const { showToastNotification } = useShowToastNotification();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[
      ETableStoragePaths.SUBSCRIBERS_NOTIFY_ME
    ] as TColumnsState[];
    if (!columnsState) {
      return Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    }
    return columnsState
      .filter(col => col.show)
      .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
      .filter(Boolean);
  }, []);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {};
      if (ids?.length) {
        filters.ids = ids;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = selectedFilters.email[0] as string;
      }
      if (selectedFilters?.cities) {
        filters.cities = selectedFilters.cities;
      }
      if (selectedFilters?.ageGroups) {
        filters.ageGroups = selectedFilters.ageGroups;
      }
      if (selectedFilters?.tags) {
        filters.tagIds = selectedFilters.tags;
      }
      return Object.keys(filters).length > 0 ? filters : undefined;
    },
    [selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      await exportNotifyMe({
        columns: exportColumns,
        ...(buildExportFilters() && { filters: buildExportFilters() }),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportNotifyMe,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportNotifyMe({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportNotifyMe,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  return (
    <>
      <PageWrapper
        title={t('TITLE')}
        isLoading={isLoading}
        error={error as TRtkErrorType}
        emptyWrapper={{
          isEmpty,
          alt: 'Notification',
          icon: EIconNames.NOTIFICATION_FILLED,
          btnText: t('empty-view.BTN_TEXT'),
          emptyText: t('empty-view.EMPTY_TEXT'),
          onClick: () => setOpen(true),
        }}
        actions={
          <Button onClick={() => setOpen(true)} color="primary" type="primary">
            {t('actions.add-subscriber.ADD_SUBSCRIBER_BTN')}
          </Button>
        }
      >
        <CardContent>
          <Table<TWorkshopSubscriberDataUI>
            headerActions={exportAction}
            onFiltersReset={onFiltersReset}
            hasFilters={hasFilters}
            currentPage={page}
            columns={columns}
            dataSource={data?.content}
            loading={isFetching}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalDataCount={data?.totalElements}
            onChange={handleTableChange}
            storagePath={ETableStoragePaths.SUBSCRIBERS_NOTIFY_ME}
            rowKey={(record: TWorkshopSubscriberDataUI) =>
              record.workshopSubscriberId
            }
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </CardContent>
      </PageWrapper>
      {open && <CreateWorkshopSubscribersModal open={open} setOpen={setOpen} />}
    </>
  );
};

export default WorkshopSubscribersList;
